import * as React from 'react';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';

import { cn } from '../../lib/utils';
import { Button } from '../Button';
import { buttonVariants } from '../Button/variants';
import type { CalendarProps, CalendarDayButtonProps } from './types';

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant = 'ghost',
  components,
  ...props
}: CalendarProps) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        'eye:group/calendar eye:bg-white eye:px-8 eye:[--cell-size:2rem] eye:[[data-slot=card-content]_&]:bg-transparent eye:[[data-slot=popover-content]_&]:bg-transparent',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      classNames={{
        root: cn('eye:w-fit', defaultClassNames.root),
        months: cn(
          'eye:relative eye:flex eye:flex-col eye:gap-4 eye:md:flex-row',
          defaultClassNames.months,
        ),
        month: cn(
          'eye:flex eye:w-full eye:flex-col eye:gap-4',
          defaultClassNames.month,
        ),
        nav: cn(
          'eye:absolute eye:inset-x eye:top-0 eye:flex eye:w-full eye:items-center eye:justify-between eye:gap-1',
          defaultClassNames.nav,
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          'eye:h-[var(--cell-size)] eye:w-[var(--cell-size)] eye:select-none eye:p-0 eye:aria-disabled:opacity-50',
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          'eye:h-[var(--cell-size)] eye:w-[var(--cell-size)] eye:select-none eye:p-0 eye:aria-disabled:opacity-50',
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          'eye:flex eye:h-[var(--cell-size)] eye:mb-4 eye:w-full eye:items-center eye:justify-center eye:px-[var(--cell-size)]',
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          'eye:flex eye:h-[var(--cell-size)] eye:w-full eye:items-center eye:justify-center eye:gap-1.5 eye:text-sm eye:font-medium',
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn(
          'eye:has-focus:border-ring eye:border-input eye:has-focus:ring-ring/50 eye:has-focus:ring-[3px] eye:relative eye:rounded-md eye:border',
          defaultClassNames.dropdown_root,
        ),
        dropdown: cn(
          'eye:bg-popover eye:absolute eye:inset-0 eye:opacity-0',
          defaultClassNames.dropdown,
        ),
        caption_label: cn(
          'eye:select-none eye:font-medium',
          captionLayout === 'label'
            ? 'eye:text-sm'
            : 'eye:[&>svg]:text-muted-foreground eye:flex eye:h-8 eye:items-center eye:gap-1 eye:rounded-md eye:pl-2 eye:pr-1 eye:text-sm eye:[&>svg]:size-3.5',
          defaultClassNames.caption_label,
        ),
        table: 'eye:w-full eye:border-collapse',
        weekdays: cn('eye:flex eye:mb-8', defaultClassNames.weekdays),
        weekday: cn(
          'eye:flex-1 eye:select-none eye:rounded-md eye:text-14 eye:font-normal',
          defaultClassNames.weekday,
        ),
        week: cn('eye:mt-2 eye:flex eye:w-full', defaultClassNames.week),
        week_number_header: cn(
          'eye:w-[var(--cell-size)] eye:select-none',
          defaultClassNames.week_number_header,
        ),
        week_number: cn(
          'eye:select-none eye:text-[0.8rem]',
          defaultClassNames.week_number,
        ),
        day: cn(
          'eye:group/day eye:relative eye:aspect-square eye:h-full eye:w-full eye:select-none eye:p-0 eye:text-center eye:[&:first-child[data-selected=true]_button]:rounded-l-md eye:[&:last-child[data-selected=true]_button]:rounded-r-md',
          defaultClassNames.day,
        ),
        range_start: cn('eye:rounded-l-md', defaultClassNames.range_start),
        range_middle: cn('eye:rounded-none', defaultClassNames.range_middle),
        range_end: cn('eye:rounded-r-md', defaultClassNames.range_end),
        today: cn(
          'eye:bg-grey-10 eye:rounded-full eye:data-[selected=true]:rounded-none',
          defaultClassNames.today,
        ),
        outside: cn(
          'eye:aria-selected:text-muted-foreground',
          defaultClassNames.outside,
        ),
        disabled: cn('eye:opacity-50', defaultClassNames.disabled),
        hidden: cn('eye:invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot='calendar'
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          );
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === 'left') {
            return <ChevronLeftIcon className={cn(className)} {...props} />;
          }

          if (orientation === 'right') {
            return <ChevronRightIcon className={cn(className)} {...props} />;
          }

          return <ChevronDownIcon className={cn(className)} {...props} />;
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className='eye:flex eye:size-[var(--cell-size)] eye:items-center eye:justify-center eye:text-center'>
                {children}
              </div>
            </td>
          );
        },
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: CalendarDayButtonProps) {
  const defaultClassNames = getDefaultClassNames();

  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <Button
      ref={ref}
      variant='ghost'
      size='icon'
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-outside={modifiers.outside}
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        'eye:group-data-[focused=true]/day:border-ring eye:group-data-[focused=true]/day:ring-ring/50 eye:font-normal eye:[&>span]:text-xs eye:data-[outside=true]:text-muted-foreground eye:flex eye:aspect-square eye:h-auto eye:w-full eye:min-w-[var(--cell-size)] eye:flex-col eye:gap-1 eye:leading-none eye:group-data-[focused=true]/day:relative eye:group-data-[focused=true]/day:z-10 eye:group-data-[focused=true]/day:ring-[3px] eye:disabled:bg-transparent eye:disabled:line-through eye:data-[disabled=true]:line-through eye:data-[outside=true]:opacity-40 eye:data-[range-end=true]:rounded-md eye:data-[range-end=true]:bg-black eye:data-[range-end=true]:text-white eye:data-[range-middle=true]:rounded-none eye:data-[range-start=true]:rounded-md eye:data-[range-start=true]:bg-black eye:data-[range-start=true]:text-white eye:data-[selected-single=true]:bg-black eye:data-[selected-single=true]:text-white eye:[&>span]:opacity-70',
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
